import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Shell from '../Shell';
import { api } from '../api';

export default function SupplierBehavior() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.supplierBehavior(id).then(setData);
  }, [id]);

  if (!data) return <Shell title="Loading…"><div className="empty">Loading</div></Shell>;

  const supplier = data.supplier || {};
  const events = data.events || [];
  const counts = data.counts || {};
  const totalEvents = events.length;
  const sent = counts.Sent || 0;
  const responded = counts.Responded || 0;
  const declined = counts.Declined || 0;
  const won = counts.Won || 0;
  const lost = counts.Lost || 0;
  const responseRate = sent > 0 ? Math.min(100, Math.round((responded / sent) * 100)) : null;
  const winRate = (won + lost) > 0 ? Math.round((won / (won + lost)) * 100) : null;

  return (
    <Shell
      title={supplier.name || 'Supplier'}
      subtitle={[
        (supplier.category || []).join(', ') || null,
        supplier.defaultIncoterm,
        supplier.defaultLocation,
      ].filter(Boolean).join(' · ') || ' '}
      breadcrumb={<><Link to="/suppliers">Suppliers</Link> / {supplier.name || ''}</>}
      actions={
        totalEvents > 0 && (
          <a
            className="btn btn-secondary btn-sm"
            href={api.supplierBehaviorExportUrl(id)}
            download={`supplier-behavior-${id}.csv`}
          >
            ⤓ Export CSV
          </a>
        )
      }
    >
      {/* KPI row */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 className="card-title">Performance (all-time)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          <Kpi value={sent} label="RFQs sent" />
          <Kpi value={responded} label="Responded" />
          <Kpi value={responseRate != null ? `${responseRate}%` : '—'} label="Response rate" />
          <Kpi value={declined} label="Declined" />
          <Kpi value={won} label="Won" />
          <Kpi value={winRate != null ? `${winRate}%` : '—'} label="Win rate" />
        </div>
      </div>

      {/* Contact card */}
      {supplier.contacts?.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h3 className="card-title">Contacts</h3>
          {supplier.contacts.map((c, i) => (
            <div key={i} style={{ fontSize: 13, padding: '6px 0', borderBottom: i < supplier.contacts.length - 1 ? '1px solid var(--border-1)' : 'none' }}>
              <b>{c.name || '—'}</b>
              {c.email && <span style={{ color: 'var(--ink-500)', marginLeft: 12 }}>{c.email}</span>}
              {c.phone && <span style={{ color: 'var(--ink-500)', marginLeft: 12 }}>{c.phone}</span>}
            </div>
          ))}
        </div>
      )}

      <h3 className="card-title">Recent events</h3>
      {totalEvents === 0 ? (
        <div className="empty" style={{ padding: 40 }}>
          <h3>No events yet</h3>
          <p style={{ color: 'var(--ink-500)' }}>
            This supplier hasn't been dispatched on any RFQ. Once you send them an RFQ from the Dispatch tab,
            their activity (Sent · Responded · Quoted · Won · Lost) will show up here.
          </p>
          <Link to="/inbox" className="btn btn-sm" style={{ marginTop: 12 }}>Go to RFQ Inbox</Link>
        </div>
      ) : (
        <table className="table">
          <thead><tr><th>When</th><th>Event</th><th>RFQ</th><th>Notes</th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e._id}>
                <td style={{ fontSize: 12, color: 'var(--ink-500)' }}>{new Date(e.at).toLocaleString()}</td>
                <td><span className={'chip ' + (
                  e.eventType === 'Won' ? 'ready'
                  : e.eventType === 'Lost' || e.eventType === 'Declined' ? 'risk'
                  : e.eventType === 'Responded' || e.eventType === 'Quoted' ? 'info'
                  : 'muted'
                )}>{e.eventType}</span></td>
                <td className="mono">{e.meta?.rfqId || '—'}</td>
                <td style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                  {e.meta?.method && <span>{e.meta.method}</span>}
                  {e.meta?.error && <span style={{ color: 'var(--danger)' }}> · err: {e.meta.error}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Shell>
  );
}

function Kpi({ value, label }) {
  return (
    <div style={{ background: 'var(--surface-1)', borderRadius: 8, padding: '12px 16px' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--ink-900)', lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--ink-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{label}</div>
    </div>
  );
}
